import { compare, Order, orderToNumber, swapOrder } from './compare';
/**
 * Sort a iterable into a fresh array.
 *
 * @param xs The iterable to sort.
 * @param makeKey The function to create the sort key. It is called for every element in `xs`
 * once. It should return a mutable array that we are allowed to change.
 * @param opts The options to use for sorting.
 * @returns A fresh array that is `xs` but sorted.
 */
export function sortBy(xs, makeKey, opts = {}) {
    // Optimization: Small arrays that are always sorted (len < 2) don't need to do any allocation.
    if (Array.isArray(xs) && xs.length < 2) {
        return xs;
    }
    const { reverse: reverseGlobally = false } = opts;
    const decorated = Array.from(xs, (x, idx) => {
        // @ts-expect-error: We mutate this array for extra efficiency.
        const res = makeKey(x);
        // Add idx to make sure we have a unique item and to make sure the
        // sorting is stable.
        res.push(reverseGlobally ? -idx : idx);
        res.push(x);
        return res;
    });
    const eq = Order.EQ;
    decorated.sort((a, b) => {
        // We don't use zip2 here to prevent an extra allocation;
        let res = eq;
        let i;
        const end = Math.min(a.length, b.length) - 1;
        for (i = 0; res === eq && i < end; ++i) {
            // @ts-expect-error: This is too magic for typescript to understand.
            const keyA = a[i];
            // @ts-expect-error: This is too magic for typescript to understand.
            const keyB = b[i];
            res = compare(keyA, keyB, opts);
        }
        let reverse;
        if (opts?.reversePerKey != null) {
            reverse = opts.reversePerKey[i - 1];
        }
        if (reverse == null) {
            reverse = opts?.reverse ?? false;
        }
        if (reverse) {
            res = swapOrder(res);
        }
        return orderToNumber(res);
    });
    return decorated.map(x => x[x.length - 1]);
}
